// frontend/src/services/eoirService.js
import axios from 'axios';

// Relative API works both locally and through the public CRM URL.
const API_URL = `${window.__CRM_API__ || `${window.location.origin}/api`}/eoir`;

// ============================================================
// FUNCIONES AUXILIARES
// ============================================================

const getAuthHeader = () => {
  // A normal session is deliberately stored in sessionStorage unless the
  // user opts in to "Mantener sesión".  Using only localStorage made the
  // EOIR workspace silently receive 401 responses and render an empty
  // portfolio after a perfectly valid login.
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// ============================================================
// NACIONALIDADES
// ============================================================

export const getNationalities = async () => {
  try {
    const response = await axios.get(`${API_URL}/nationalities`, {
      headers: getAuthHeader(),
      timeout: 10000
    });
    return response.data.nationalities || [];
  } catch (error) {
    console.error('Error obteniendo nacionalidades:', error);
    return [];
  }
};

// ============================================================
// CONSULTA EAD CLOCK
// ============================================================

export const consultEADClock = async (
  aNumber,
  nationality,
  clienteId = null,
  onProgress = null
) => {
  try {
    // Validar entrada
    if (!aNumber || aNumber.trim() === '') {
      throw new Error('A-Number es requerido');
    }
    
    if (!nationality || nationality.trim() === '') {
      throw new Error('Nacionalidad es requerida');
    }
    
    // Reportar progreso
    if (onProgress) onProgress('Validando datos...', 10);
    
    // Limpiar A-Number
    const cleanANumber = aNumber.replace(/[^0-9]/g, '');
    if (cleanANumber.length < 8 || cleanANumber.length > 9) {
      throw new Error(`A-Number debe tener 8 o 9 dígitos. Recibido: ${cleanANumber.length} dígitos`);
    }
    
    if (onProgress) onProgress('Conectando con EOIR...', 30);
    
    // Realizar consulta
    const response = await axios.post(
      `${API_URL}/consult-ead`,
      {
        a_number: aNumber,
        nationality: nationality,
        cliente_id: clienteId
      },
      {
        headers: getAuthHeader(),
        timeout: 45000 // 45 segundos
      }
    );
    
    if (onProgress) onProgress('Procesando resultados...', 80);
    
    // Procesar respuesta
    const result = response.data;
    
    if (result.success) {
      if (onProgress) onProgress('✅ Caso encontrado', 100);
      return {
        success: true,
        data: result.data,
        message: result.message
      };
    } else {
      if (onProgress) onProgress('❌ ' + result.message, 100);
      throw new Error(result.error || result.message || 'Error desconocido');
    }
    
  } catch (error) {
    if (axios.isCancel(error)) {
      throw new Error('La consulta fue cancelada');
    }
    
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      throw new Error('La consulta está tomando demasiado tiempo. Por favor, intenta nuevamente.');
    }
    
    if (error.response) {
      const serverError = error.response.data;
      throw new Error(serverError.error || serverError.message || 'Error del servidor');
    }
    
    if (error.request) {
      throw new Error('No se pudo conectar con el servidor. Verifica tu conexión a internet.');
    }
    
    throw new Error(error.message || 'Error desconocido');
  }
};

// ============================================================
// DASHBOARD STATS
// ============================================================

export const getDashboardStats = async () => {
  try {
    const response = await axios.get(`${API_URL}/dashboard/stats`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error obteniendo dashboard stats:', error);
    // Retornar datos vacíos en caso de error para no romper el frontend
    return {
      total_cases: 0,
      active_cases: 0,
      pending_cases: 0,
      closed_cases: 0,
      upcoming_hearings: 0,
      ead_eligible: 0,
      total_judges: 0
    };
  }
};

// ============================================================
// CASOS CRUD
// ============================================================

export const getCases = async (params = {}) => {
  try {
    const response = await axios.get(`${API_URL}/cases`, {
      params,
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error obteniendo casos:', error);
    return [];
  }
};

export const getCaseById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/cases/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error obteniendo caso:', error);
    throw error;
  }
};

export const createCase = async (caseData) => {
  try {
    const response = await axios.post(`${API_URL}/cases`, caseData, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error creando caso:', error);
    throw error;
  }
};

export const updateCase = async (id, caseData) => {
  try {
    // Optional dates must be null, never an empty string: FastAPI/Pydantic
    // correctly rejects an empty string as a date.
    const payload = Object.fromEntries(Object.entries(caseData || {}).map(([key, value]) => [
      key, ['next_hearing_date'].includes(key) && value === '' ? null : value
    ]));
    const response = await axios.put(`${API_URL}/cases/${id}`, payload, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error actualizando caso:', error);
    throw error;
  }
};

export const deleteCase = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/cases/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error eliminando caso:', error);
    throw error;
  }
};

// ============================================================
// ESTADÍSTICAS DE JUECES
// ============================================================

export const syncCase = async (id) => {
  const response = await axios.post(`${API_URL}/cases/${id}/sync`, {}, { headers: getAuthHeader(), timeout: 90000 });
  return response.data;
};

// ACIS is an assisted official-source flow: the CRM parses a result the user
// has reviewed in ACIS, compares it, and persists it only after confirmation.
export const importAcisResult = async (id, text) => {
  const response = await axios.post(
    `${API_URL}/cases/${id}/acis-import`,
    { text },
    { headers: getAuthHeader(), timeout: 30000 }
  );
  return response.data;
};

export const confirmAcisResult = async (id, detected) => {
  const response = await axios.post(
    `${API_URL}/cases/${id}/acis-confirm`,
    { detected },
    { headers: getAuthHeader(), timeout: 30000 }
  );
  return response.data;
};

export const getJudgeStats = async (courtLocation = null) => {
  try {
    const params = courtLocation ? { court_location: courtLocation } : {};
    const response = await axios.get(`${API_URL}/judge-stats`, {
      params,
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error obteniendo estadísticas de jueces:', error);
    return [];
  }
};

// ============================================================
// HEALTH CHECK
// ============================================================

export const checkHealth = async () => {
  try {
    const response = await axios.get(`${API_URL}/health`, {
      headers: getAuthHeader(),
      timeout: 5000
    });
    return response.data;
  } catch (error) {
    console.error('Health check falló:', error);
    return { status: 'unhealthy' };
  }
};

// ============================================================
// EXPORTACIÓN POR DEFECTO
// ============================================================

const eoirService = {
  getNationalities,
  consultEADClock,
  getDashboardStats,
  getCases,
  getCaseById,
  createCase,
  updateCase,
  deleteCase,
  importAcisResult,
  confirmAcisResult,
  getJudgeStats,
  checkHealth
};

export default eoirService;
